import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { billsApi } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, ExternalLink, Download, Copy, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths } from 'date-fns'

interface ForecastItem {
  bill_id: number
  bill_name: string
  amount: number
  due_date: string
  category_name: string | null
  category_color: string | null
  autopay: boolean
  url: string | null
}

interface ForecastResponse {
  forecast: ForecastItem[]
  monthlyTotals: Record<string, number>
  totalAmount: number
}

export default function Calendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [isExportOpen, setIsExportOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const getIcalUrl = () => {
    const token = localStorage.getItem('token')
    return `${window.location.origin}/api/ical/bills?token=${token}`
  }

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(getIcalUrl())
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      console.error('Failed to copy:', e)
    }
  }

  const handleDownloadIcal = () => {
    window.open(getIcalUrl(), '_blank')
  }

  const { data } = useQuery<ForecastResponse>({
    queryKey: ['bills', 'forecast'],
    queryFn: async () => {
      const res = await billsApi.getForecast(6)
      return res.data
    },
  })

  const forecast = data?.forecast || []
  const monthlyTotals = data?.monthlyTotals || {}

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd })

  // Pad days to start on Sunday
  const startDay = monthStart.getDay()
  const paddedDays: (Date | null)[] = [...Array(startDay).fill(null), ...days]

  // Get bills for a specific day
  const getBillsForDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return forecast.filter(item => item.due_date === dateStr)
  }

  const currentMonthKey = format(currentMonth, 'yyyy-MM')
  const monthTotal = monthlyTotals[currentMonthKey] || 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Bill Calendar</h1>
          <p className="text-muted-foreground">Upcoming bill due dates</p>
        </div>
        <Dialog open={isExportOpen} onOpenChange={setIsExportOpen}>
          <DialogTrigger asChild>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export to Calendar
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Export to Calendar</DialogTitle>
              <DialogDescription>
                Add your bills to your calendar app (Google Calendar, Apple Calendar, Outlook, etc.)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Calendar Subscription URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={getIcalUrl()}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button variant="outline" size="icon" onClick={handleCopyUrl}>
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Paste this URL in your calendar app to subscribe. The calendar will update automatically.
                </p>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm">Or download a snapshot:</span>
                <Button variant="secondary" onClick={handleDownloadIcal}>
                  <Download className="mr-2 h-4 w-4" />
                  Download .ics
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <CardTitle className="text-xl">
              {format(currentMonth, 'MMMM yyyy')}
            </CardTitle>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Month Total</p>
            <p className="text-2xl font-bold">${monthTotal.toFixed(2)}</p>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {paddedDays.map((day, i) => {
              if (!day) {
                return <div key={`empty-${i}`} className="min-h-[100px]" />
              }

              const bills = getBillsForDay(day)
              const dayTotal = bills.reduce((sum, b) => sum + b.amount, 0)

              return (
                <div
                  key={day.toISOString()}
                  className={`min-h-[100px] p-2 border rounded-lg ${
                    isToday(day) ? 'bg-primary/10 border-primary' : 'border-border'
                  } ${!isSameMonth(day, currentMonth) ? 'opacity-50' : ''}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className={`text-sm font-medium ${isToday(day) ? 'text-primary' : ''}`}>
                      {format(day, 'd')}
                    </span>
                    {dayTotal > 0 && (
                      <span className="text-xs font-medium text-muted-foreground">
                        ${dayTotal.toFixed(0)}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {bills.slice(0, 3).map((bill, idx) => (
                      <div
                        key={`${bill.bill_id}-${idx}`}
                        className="text-xs p-1 rounded truncate flex items-center gap-1"
                        style={{
                          backgroundColor: bill.category_color ? `${bill.category_color}20` : undefined,
                          borderLeft: bill.category_color ? `3px solid ${bill.category_color}` : undefined
                        }}
                        title={`${bill.bill_name}: $${bill.amount}`}
                      >
                        <span className="truncate flex-1">{bill.bill_name}</span>
                        {bill.autopay && <Badge variant="outline" className="text-[10px] px-1">Auto</Badge>}
                      </div>
                    ))}
                    {bills.length > 3 && (
                      <div className="text-xs text-muted-foreground">
                        +{bills.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Next 30 Days
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {forecast
              .filter(item => {
                const dueDate = new Date(item.due_date)
                const thirtyDaysFromNow = addMonths(new Date(), 1)
                return dueDate <= thirtyDaysFromNow
              })
              .map((item, idx) => (
                <div
                  key={`${item.bill_id}-${item.due_date}-${idx}`}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.category_color || '#888' }}
                    />
                    <div>
                      <p className="font-medium">{item.bill_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(item.due_date), 'EEEE, MMM d')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-medium">${item.amount.toFixed(2)}</p>
                      {item.autopay && (
                        <Badge variant="secondary" className="text-xs">Autopay</Badge>
                      )}
                    </div>
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            {forecast.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No upcoming bills
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
